import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Stack,
  Tooltip,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Assessment as ReportIcon,
  Search as SearchIcon,
  Receipt as ReceiptIcon,
  CurrencyRupee as MoneyIcon,
  TrendingUp as ProfitIcon,
  Print as PrintIcon,
  FilterList as FilterIcon,
  CalendarMonth as CalendarIcon,
  PieChart as PieChartIcon,
  TableChart as TableChartIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { BillReportSummary } from '../types/Report';
import { reportService } from '../services/reportService';
import { formatCurrency, formatDate } from '../utils/formatters';

const ReportsPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';

  // Date range state — default to current month
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const [startDate, setStartDate] = useState(firstDay.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(now.toISOString().split('T')[0]);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('');

  // Report data
  const [report, setReport] = useState<BillReportSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Section tab: 0 = Daily Breakdown, 1 = All Bills
  const [activeTab, setActiveTab] = useState(0);

  const fetchReport = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }
    if (new Date(endDate) < new Date(startDate)) {
      setError('End date must be after start date');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await reportService.getBillingReport(
        startDate,
        endDate,
        statusFilter || undefined,
        paymentMethodFilter || undefined
      );
      setReport(data);
    } catch (err: any) {
      setError(err.response?.data || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Paid': return 'success';
      case 'Pending': return 'warning';
      case 'Cancelled': return 'error';
      case 'Overdue': return 'error';
      default: return 'default';
    }
  };

  // Quick date range presets
  const setPreset = (preset: string) => {
    const today = new Date();
    let start: Date;
    let end: Date = today;
    switch (preset) {
      case 'today':
        start = today;
        break;
      case 'yesterday':
        start = new Date(today);
        start.setDate(start.getDate() - 1);
        end = new Date(start);
        break;
      case 'thisWeek':
        start = new Date(today);
        start.setDate(start.getDate() - start.getDay());
        break;
      case 'lastWeek':
        start = new Date(today);
        start.setDate(start.getDate() - start.getDay() - 7);
        end = new Date(start);
        end.setDate(end.getDate() + 6);
        break;
      case 'thisMonth':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'lastMonth':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'last3Months':
        start = new Date(today.getFullYear(), today.getMonth() - 3, 1);
        break;
      default:
        return;
    }
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #2C7A9C 0%, #4A9BBD 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ReportIcon sx={{ color: 'white', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Reports
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Generate and view billing reports for any period
            </Typography>
          </Box>
        </Box>
        {report && (
          <Tooltip title="Print Report">
            <IconButton onClick={handlePrint} sx={{ color: '#2C7A9C' }}>
              <PrintIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Filters Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <FilterIcon sx={{ color: '#2C7A9C' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Report Filters
            </Typography>
          </Box>

          {/* Quick Presets */}
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Chip label="Today" variant="outlined" onClick={() => setPreset('today')} />
            <Chip label="Yesterday" variant="outlined" onClick={() => setPreset('yesterday')} />
            <Chip label="This Week" variant="outlined" onClick={() => setPreset('thisWeek')} />
            <Chip label="Last Week" variant="outlined" onClick={() => setPreset('lastWeek')} />
            <Chip label="This Month" variant="outlined" onClick={() => setPreset('thisMonth')} color="primary" />
            <Chip label="Last Month" variant="outlined" onClick={() => setPreset('lastMonth')} />
            <Chip label="Last 3 Months" variant="outlined" onClick={() => setPreset('last3Months')} />
          </Stack>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Start Date"
                type="date"
                fullWidth
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="End Date"
                type="date"
                fullWidth
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Paid">Paid</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={paymentMethodFilter}
                  label="Payment Method"
                  onChange={(e) => setPaymentMethodFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Cash">Cash</MenuItem>
                  <MenuItem value="Card">Card</MenuItem>
                  <MenuItem value="UPI">UPI</MenuItem>
                  <MenuItem value="Insurance">Insurance</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                fullWidth
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
                onClick={fetchReport}
                disabled={loading}
                sx={{ height: 40 }}
              >
                {loading ? 'Generating...' : 'Generate Report'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Report Results */}
      {report && (
        <>
          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: 'white', border: '1px solid #e0e0e0' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <ReceiptIcon sx={{ color: '#2C7A9C' }} />
                    <Typography variant="body2" color="text.secondary">Total Bills</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#2C7A9C' }}>{report.totalBills}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {formatDate(report.startDate)} – {formatDate(report.endDate)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: 'white', border: '1px solid #e0e0e0' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <MoneyIcon sx={{ color: '#10b981' }} />
                    <Typography variant="body2" color="text.secondary">Total Amount</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#10b981' }}>{formatCurrency(report.totalAmount)}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Paid: {formatCurrency(report.totalPaidAmount)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: 'white', border: '1px solid #e0e0e0' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <MoneyIcon sx={{ color: '#f59e0b' }} />
                    <Typography variant="body2" color="text.secondary">Pending</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#f59e0b' }}>{formatCurrency(report.totalPendingAmount)}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {report.pendingBillsCount} bills pending
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: 'white', border: '1px solid #e0e0e0' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <ProfitIcon sx={{ color: '#8b5cf6' }} />
                    <Typography variant="body2" color="text.secondary">Medicine Profit</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#8b5cf6' }}>{formatCurrency(report.totalMedicineProfit)}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Cost: {formatCurrency(report.totalMedicineCost)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Breakdowns side by side */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Payment Method Breakdown */}
            {report.paymentMethodBreakdown.length > 0 && (
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <PieChartIcon sx={{ color: '#2C7A9C' }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>Payment Method Breakdown</Typography>
                    </Box>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ background: '#1a1a1a' }}>
                            <TableCell sx={{ fontWeight: 600, color: 'white' }}>Payment Method</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600, color: 'white' }}>Bills</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: 'white' }}>Amount</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {report.paymentMethodBreakdown.map((pm) => (
                            <TableRow key={pm.paymentMethod}>
                              <TableCell>
                                <Chip label={pm.paymentMethod || 'N/A'} size="small" variant="outlined" />
                              </TableCell>
                              <TableCell align="center">{pm.count}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>
                                {formatCurrency(pm.amount)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Item Type Breakdown */}
            {report.itemTypeBreakdown.length > 0 && (
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <PieChartIcon sx={{ color: '#8b5cf6' }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>Item Type Breakdown</Typography>
                    </Box>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ background: '#1a1a1a' }}>
                            <TableCell sx={{ fontWeight: 600, color: 'white' }}>Item Type</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600, color: 'white' }}>Quantity</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: 'white' }}>Amount</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {report.itemTypeBreakdown.map((it) => (
                            <TableRow key={it.itemType}>
                              <TableCell>
                                <Chip label={it.itemType || 'N/A'} size="small" color="secondary" variant="outlined" />
                              </TableCell>
                              <TableCell align="center">{it.quantity}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>
                                {formatCurrency(it.totalAmount)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>

          {/* Section Tabs */}
          <Card sx={{ mb: 3 }}>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
            >
              <Tab
                icon={<CalendarIcon />}
                iconPosition="start"
                label="Daily Breakdown"
                sx={{ textTransform: 'none', fontWeight: 600 }}
              />
              <Tab
                icon={<TableChartIcon />}
                iconPosition="start"
                label={`All Bills (${report.bills.length})`}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              />
            </Tabs>

            <CardContent>
              {/* Daily Breakdown Tab */}
              {activeTab === 0 && (
                <>
                  {report.dailyBreakdown.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ background: '#1a1a1a' }}>
                            <TableCell sx={{ fontWeight: 600, color: 'white' }}>Date</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600, color: 'white' }}>Bills</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: 'white' }}>Total Amount</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: 'white' }}>Paid</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: 'white' }}>Discount</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: 'white' }}>Med. Cost</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: 'white' }}>Med. Profit</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {report.dailyBreakdown.map((day) => (
                            <TableRow key={day.date} hover>
                              <TableCell>{formatDate(day.date)}</TableCell>
                              <TableCell align="center">{day.billCount}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>
                                {formatCurrency(day.totalAmount)}
                              </TableCell>
                              <TableCell align="right" sx={{ color: '#10b981' }}>
                                {formatCurrency(day.paidAmount)}
                              </TableCell>
                              <TableCell align="right" sx={{ color: '#f59e0b' }}>
                                {formatCurrency(day.discountAmount)}
                              </TableCell>
                              <TableCell align="right" sx={{ color: '#ef4444' }}>
                                {formatCurrency(day.medicineCostAmount)}
                              </TableCell>
                              <TableCell align="right" sx={{ color: '#8b5cf6', fontWeight: 600 }}>
                                {formatCurrency(day.medicineProfit)}
                              </TableCell>
                            </TableRow>
                          ))}
                          {/* Totals row */}
                          <TableRow sx={{ background: '#f0f9f9' }}>
                            <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700 }}>
                              {report.dailyBreakdown.reduce((sum, d) => sum + d.billCount, 0)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              {formatCurrency(report.dailyBreakdown.reduce((sum, d) => sum + d.totalAmount, 0))}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: '#10b981' }}>
                              {formatCurrency(report.dailyBreakdown.reduce((sum, d) => sum + d.paidAmount, 0))}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: '#f59e0b' }}>
                              {formatCurrency(report.dailyBreakdown.reduce((sum, d) => sum + d.discountAmount, 0))}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: '#ef4444' }}>
                              {formatCurrency(report.dailyBreakdown.reduce((sum, d) => sum + d.medicineCostAmount, 0))}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: '#8b5cf6' }}>
                              {formatCurrency(report.dailyBreakdown.reduce((sum, d) => sum + d.medicineProfit, 0))}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">No daily data for the selected period</Typography>
                    </Box>
                  )}
                </>
              )}

              {/* All Bills Tab */}
              {activeTab === 1 && (
                <TableContainer sx={{ maxHeight: 500 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ '& .MuiTableCell-head': { background: '#1a1a1a', color: 'white', fontWeight: 600 } }}>
                        <TableCell>Bill #</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Patient</TableCell>
                        <TableCell>Created By</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell align="right">Discount</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="center">Status</TableCell>
                        <TableCell>Payment</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report.bills.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">No bills found for the selected period</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        report.bills.map((bill) => (
                          <TableRow key={bill.billId} hover>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#2C7A9C' }}>
                                {bill.billNumber}
                              </Typography>
                            </TableCell>
                            <TableCell>{formatDate(bill.billDate)}</TableCell>
                            <TableCell>{bill.patientName}</TableCell>
                            <TableCell>{bill.createdByUserName || '—'}</TableCell>
                            <TableCell align="right">{formatCurrency(bill.subTotal)}</TableCell>
                            <TableCell align="right">
                              {bill.discountAmount > 0 ? formatCurrency(bill.discountAmount) : '—'}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              {formatCurrency(bill.totalAmount)}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={bill.status}
                                size="small"
                                color={getStatusColor(bill.status) as any}
                              />
                            </TableCell>
                            <TableCell>{bill.paymentMethod || '—'}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* Additional summary info */}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Report generated for period: {formatDate(report.startDate)} – {formatDate(report.endDate)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Discount: {formatCurrency(report.totalDiscountAmount)} | Total Tax: {formatCurrency(report.totalTaxAmount)}
            </Typography>
          </Box>
        </>
      )}

      {/* Empty state */}
      {!report && !loading && !error && (
        <Card sx={{ mt: 2 }}>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <ReportIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Report Generated
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select a date range and click "Generate Report" to view billing reports.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ReportsPage;
